// INFRAIMPACT public dashboard bootstrap.
// Wires the static dashboard (assets/js/*.js) to the live Worker/D1 API so that
// Studio-validated rows become the source of truth instead of the seed JSON.
//
// Validation gates (public visibility only — the Worker enforces these server-side):
//   assets               → .is('deleted_at', null)
//   yearly_observations  → .in('validation_status', ['field_confirmed','corrected'])
//   indicators           → .eq('lifecycle_status', 'active')

import type { SupaWrapper } from '../admin/supabase-client';
import { PUBLIC_DICT, wireLangToggle, applyI18n, getLocale } from './i18n';

type Row = Record<string, unknown>;

interface DbRemote {
  getAll(table: string): Promise<Row[]>;
}

// window.IFI is declared globally in src/admin/main.ts. We piggyback on the
// same shape (writing only `supa` and `dbRemote`), so no redeclaration here.

const PAGE_SIZE = 1000;

interface ProviderReady {
  ready: boolean;
  issue?: string;
}

interface IfiGlobal {
  publicDict?: typeof PUBLIC_DICT;
  studio?: Record<string, unknown>;
  publicDataProviderReady?: Promise<ProviderReady>;
}

let resolveProviderReady: ((value: ProviderReady) => void) | null = null;
const publicDataProviderReady = new Promise<ProviderReady>((resolve) => {
  resolveProviderReady = resolve;
});

// Singleton config tables (migration 0014). Each has exactly one row keyed
// id='singleton' with a JSONB payload column. dbRemote.getAll returns the
// 1-element array and data-loader.js unwraps to the inner payload.
const SINGLETON_TABLES = new Set<string>([
  'dashboard_weights',
  'dashboard_thresholds',
  'dashboard_metadata',
]);

async function getAll(supa: SupaWrapper, table: string): Promise<Row[]> {
  if (!supa.client) throw new Error('supabase client not ready');

  // Singleton fast path — single .single() fetch, no pagination.
  if (SINGLETON_TABLES.has(table)) {
    const { data, error } = await supa.client
      .from(table)
      .select('*')
      .eq('id', 'singleton')
      .maybeSingle();
    if (error) throw error;
    return data ? [data as Row] : [];
  }

  let q = supa.client.from(table).select('*');
  if (table === 'assets') {
    q = q.is('deleted_at', null);
  } else if (table === 'yearly_observations') {
    q = q.in('validation_status', ['field_confirmed', 'corrected']);
  } else if (table === 'indicators') {
    q = q.eq('lifecycle_status', 'active');
  }
  const out: Row[] = [];
  let from = 0;
  while (true) {
    const { data, error } = await q.range(from, from + PAGE_SIZE - 1);
    if (error) throw error;
    if (!data || data.length === 0) break;
    out.push(...(data as Row[]));
    if (data.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }
  return out;
}

async function bootstrap(): Promise<ProviderReady> {
  // Phase O: public dashboard ALWAYS prefers Supabase if the build embeds a
  // valid URL + anon key. The `'supabase'` preference bypasses the
  // localStorage gate that the Studio side relies on — Studio remains
  // untouched because it calls createSupa() with no args (default 'auto').
  const { createSupa } = await import('../admin/supabase-client');
  const supa = createSupa({ storageModePreference: 'supabase' });
  if (!supa.configured || !supa.client) {
    console.warn('[public] Live DB not configured — falling back to data/*.json',
                 supa.configIssue);
  }
  const dbRemote: DbRemote = { getAll: (t) => getAll(supa, t) };
  const globalIfi = ((window as unknown as { IFI?: IfiGlobal }).IFI =
    (window as unknown as { IFI?: IfiGlobal }).IFI ?? { studio: {} });
  globalIfi.studio = globalIfi.studio ?? {};
  globalIfi.studio.supa = supa;
  globalIfi.studio.dbRemote = dbRemote;
  const detail = {
    ready: !!(supa.storageMode === 'supabase' && supa.client),
    issue: supa.configIssue ?? undefined,
  };
  resolveProviderReady?.(detail);
  window.dispatchEvent(new CustomEvent('ifi:supabase-ready', { detail }));
  return detail;
}

interface LiteratureUsedEntry {
  id: string;
  authorYear: string;
  year?: number | null;
  source?: string;
  themes?: string[];
}

interface LiteratureUsedPayload {
  themes: Record<string, string>;
  themeCounts: Record<string, number>;
  entries: LiteratureUsedEntry[];
}

// Theme cards for the homepage summary. Must match the 5 curated themes in
// docs/LITERATURE_REVIEW_USED_IN_THESIS.md (and themes object in
// data/literature-review-used.json). Do NOT add themes from the full master.
const _HOMEPAGE_THEMES: Array<{ id: string; label: { id: string; en: string }; desc: { id: string; en: string } }> = [
  { id: 'ex_post',
    label: { id: 'Ex-post evaluation dan project success', en: 'Ex-post evaluation and project success' },
    desc:  { id: 'Kerangka penilaian pasca-konstruksi untuk learning loop investasi publik.', en: 'Post-construction evaluation frameworks as a learning loop for public investment.' } },
  { id: 'fiscal_decentral',
    label: { id: 'Fiscal decentralization dan performance grants', en: 'Fiscal decentralization and performance grants' },
    desc:  { id: 'Desain transfer fiskal dan kapasitas pemerintah daerah membiayai O&M.', en: 'Intergovernmental transfer design and local capacity to fund O&M.' } },
  { id: 'agency_voids',
    label: { id: 'Institutional voids, agency problem, dan moral hazard', en: 'Institutional voids, agency problem, and moral hazard' },
    desc:  { id: 'Kekosongan kelembagaan, soft budget constraint, dan asimetri informasi pusat–daerah.', en: 'Institutional voids, soft budget constraints, and central–local information asymmetry.' } },
  { id: 'asset_lifecycle',
    label: { id: 'Infrastructure asset management dan sanitation lifecycle', en: 'Infrastructure asset management and sanitation lifecycle' },
    desc:  { id: 'Runtime IAM, post-construction support, dan deteriorasi aset sanitasi.', en: 'Runtime IAM, post-construction support, and sanitation asset deterioration.' } },
  { id: 'bwm_indicator',
    label: { id: 'BWM, composite indicator, CVI/Aiken, dan sensitivity analysis', en: 'BWM, composite indicator, CVI/Aiken, and sensitivity analysis' },
    desc:  { id: 'Pembobotan ahli (Rezaei 2015), indeks komposit, dan robustness check.', en: 'Expert weighting (Rezaei 2015), composite indices, and robustness checks.' } },
];

type AcademicFoundation = {
  id: string;
  themeId: string;
  label: { id: string; en: string };
  desc: { id: string; en: string };
  primaryReferenceIds: string[];
  secondaryReferenceIds?: string[];
};

const ACADEMIC_FOUNDATIONS: AcademicFoundation[] = [
  {
    id: 'ex_post',
    themeId: 'ex_post',
    label: { id: 'Ex-post evaluation dan project success', en: 'Ex-post evaluation and project success' },
    desc: { id: 'Kerangka penilaian pasca-konstruksi untuk learning loop investasi publik.', en: 'Post-construction evaluation frameworks as a learning loop for public investment.' },
    primaryReferenceIds: ['lit-used-10', 'lit-used-43'],
    secondaryReferenceIds: ['lit-used-3', 'lit-used-130'],
  },
  {
    id: 'fiscal_decentral',
    themeId: 'fiscal_decentral',
    label: { id: 'Fiscal decentralization dan performance grants', en: 'Fiscal decentralization and performance grants' },
    desc: { id: 'Desain transfer fiskal dan kapasitas pemerintah daerah membiayai O&M.', en: 'Intergovernmental transfer design and local capacity to fund O&M.' },
    primaryReferenceIds: ['lit-used-12-136', 'lit-used-add-05-1'],
    secondaryReferenceIds: ['lit-used-add-06-1', 'lit-used-253'],
  },
  {
    id: 'agency_voids',
    themeId: 'agency_voids',
    label: { id: 'Institutional voids, agency problem, dan moral hazard', en: 'Institutional voids, agency problem, and moral hazard' },
    desc: { id: 'Kekosongan kelembagaan, soft budget constraint, dan asimetri informasi pusat-daerah.', en: 'Institutional voids, soft budget constraints, and central-local information asymmetry.' },
    primaryReferenceIds: ['lit-used-109', 'lit-used-132'],
    secondaryReferenceIds: ['lit-used-135', 'lit-used-add-01-1'],
  },
  {
    id: 'asset_lifecycle',
    themeId: 'asset_lifecycle',
    label: { id: 'Infrastructure asset management dan sanitation lifecycle', en: 'Infrastructure asset management and sanitation lifecycle' },
    desc: { id: 'Runtime IAM, post-construction support, dan deteriorasi aset sanitasi.', en: 'Runtime IAM, post-construction support, and sanitation asset deterioration.' },
    primaryReferenceIds: ['lit-used-173', 'lit-used-181'],
    secondaryReferenceIds: ['lit-used-201', 'lit-used-add-07-1', 'lit-used-2'],
  },
  {
    id: 'bwm_indicator',
    themeId: 'bwm_indicator',
    label: { id: 'BWM, composite indicator, CVI/Aiken, dan sensitivity analysis', en: 'BWM, composite indicator, CVI/Aiken, and sensitivity analysis' },
    desc: { id: 'Pembobotan ahli (Rezaei 2015), indeks komposit, dan robustness check.', en: 'Expert weighting (Rezaei 2015), composite indices, and robustness checks.' },
    primaryReferenceIds: ['lit-used-148', 'lit-used-171'],
    secondaryReferenceIds: ['lit-used-230', 'lit-used-add-10-2', 'lit-used-add-11-1'],
  },
];

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (char) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  })[char] ?? char);
}

// Phase O: pick exactly ONE key reference per foundation. Curator-chosen
// primary IDs win; fall back to first entry with the matching theme tag.
function pickKeyReference(foundation: AcademicFoundation, entries: LiteratureUsedEntry[]): LiteratureUsedEntry | null {
  const byId = new Map(entries.map((entry) => [entry.id, entry]));
  for (const id of foundation.primaryReferenceIds) {
    const hit = byId.get(id);
    if (hit) return hit;
  }
  return entries.find((entry) => (entry.themes ?? []).includes(foundation.themeId)) ?? null;
}

async function loadLiteratureAndRenderHomepage(): Promise<void> {
  const host = document.getElementById('academicFoundationGrid');
  if (!host) return;
  let payload: LiteratureUsedPayload | null = null;
  try {
    const res = await fetch('data/literature-review-used.json', { cache: 'force-cache' });
    if (res.ok) payload = (await res.json()) as LiteratureUsedPayload;
  } catch (e) {
    console.warn('[public] literature-review-used.json fetch failed', e);
  }
  const loc = getLocale();
  const counts = payload?.themeCounts ?? {};
  const entries = payload?.entries ?? [];
  const studioHref = 'admin.html#literature-review';
  const keyLabel = PUBLIC_DICT['academic.key_reference']?.[loc] ?? 'Key reference';
  const moreLabel = PUBLIC_DICT['academic.more_in_studio']?.[loc] ?? 'more in Studio';
  const refsLabel = PUBLIC_DICT['academic.refs_count']?.[loc] ?? 'references';

  host.innerHTML = ACADEMIC_FOUNDATIONS.map((t) => {
    const count = counts[t.themeId] ?? 0;
    const key = pickKeyReference(t, entries);
    const remaining = Math.max(0, count - (key ? 1 : 0));
    const keyHtml = key
      ? `<strong>${escapeHtml(keyLabel)}:</strong> ${escapeHtml(key.authorYear)}.`
      : `<strong>${escapeHtml(keyLabel)}:</strong> —`;
    const moreHtml = remaining > 0
      ? ` <a class="academic-list__more" href="${studioHref}">+${remaining} ${escapeHtml(moreLabel)} →</a>`
      : '';
    return `
      <li class="academic-list__item">
        <div class="academic-list__head">
          <h3 class="academic-list__title">${escapeHtml(t.label[loc])}</h3>
          <span class="academic-list__count">${count} ${escapeHtml(refsLabel)}</span>
        </div>
        <p class="academic-list__desc">${escapeHtml(t.desc[loc])}</p>
        <p class="academic-list__key">${keyHtml}${moreHtml}</p>
      </li>`;
  }).join('');
}

// applyI18n synchronous — swaps static data-i18n-key text immediately so
// Indonesian default text is correct on first paint and doesn't shift.
applyI18n(PUBLIC_DICT);
wireLangToggle(PUBLIC_DICT);

// Phase N: expose PUBLIC_DICT to the classic assets/js/app.js bundle so its
// map placeholder state machine can localise its dynamic text without
// duplicating the dictionary. Safe read-only handle.
const ifi = ((window as unknown as { IFI?: IfiGlobal }).IFI = (window as unknown as { IFI?: IfiGlobal }).IFI ?? {});
ifi.publicDict = PUBLIC_DICT;
ifi.publicDataProviderReady = publicDataProviderReady;
// Phase O fix: do NOT call bootstrap() synchronously here. createSupa()
// dynamically pulls in @supabase/supabase-js (~210 KB) + validates the
// config; running it on the critical path adds ~350 ms to TBT on mobile
// CPU emulation (Lighthouse mobile Perf 95 → 87 regression). Bootstrap is
// scheduled inside the requestIdleCallback at the end of this file so the
// JS lands well after first paint. Any consumer that needs the wrapper
// awaits window.IFI.publicDataProviderReady (a Promise that resolves
// inside bootstrap()).

// Phase N: collapsible "Academic Foundation" accordion. Toggle aria-expanded,
// flip the hidden attribute on the detail container, and swap the button label.
// If the literature payload hasn't populated yet (idle preload hasn't fired
// when the user clicks), kick off the fetch so the grid isn't empty.
function wireAcademicFoundationToggle(): void {
  const btn = document.getElementById('academicFoundationToggle');
  const detail = document.getElementById('academicFoundationDetail');
  const grid = document.getElementById('academicFoundationGrid');
  if (!btn || !detail) return;
  btn.addEventListener('click', () => {
    const expanded = btn.getAttribute('aria-expanded') === 'true';
    const next = !expanded;
    btn.setAttribute('aria-expanded', String(next));
    detail.hidden = !next;
    const loc = getLocale();
    detail.classList.toggle('academic-summary__detail--open', next);
    const key = next ? 'academic.hideFoundation' : 'academic.viewFoundation';
    btn.setAttribute('data-i18n-key', key);
    const entry = PUBLIC_DICT[key];
    if (entry) btn.textContent = entry[loc];
    if (next && grid && grid.children.length === 0) {
      void loadLiteratureAndRenderHomepage();
    }
  });
}
wireAcademicFoundationToggle();

// Phase M: defer Supabase wrapper creation + literature fetch to idle time
// so neither blocks LCP. createSupa() pulls in @supabase/supabase-js
// (lazy-imported by the bundle), and the literature JSON fetch is a network
// request we don't need before first paint.
type IdleCb = (cb: () => void, opts?: { timeout?: number }) => number;
const ric: IdleCb =
  (window as unknown as { requestIdleCallback?: IdleCb }).requestIdleCallback ??
  ((cb: () => void) => (setTimeout(cb, 0) as unknown as number));

ric(() => {
  void bootstrap();
  void loadLiteratureAndRenderHomepage();
}, { timeout: 2000 });
